import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MiniSparkline } from './ChartComponents';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export function StatsCard({ 
  title, 
  value, 
  description, 
  icon: Icon, 
  colorClass,
  trend,
  trendData,
  index = 0
}) {
  // Animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1, // Staggered animation
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  const valueVariants = {
    hidden: { 
      scale: 0.8,
      opacity: 0 
    },
    visible: { 
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        delay: index * 0.1 + 0.2,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  // Determine trend icon and color
  const getTrendIcon = () => {
    if (!trend) return null;
    
    if (trend > 0) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (trend < 0) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getTrendText = () => {
    if (!trend) return null;
    
    const trendColor = trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600';
    const trendSign = trend > 0 ? '+' : '';
    
    return (
      <span className={`text-xs font-medium ${trendColor}`}>
        {trendSign}{trend}
      </span>
    );
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ 
        y: -4,
        transition: { duration: 0.2 }
      }}
    >
      <Card className="relative overflow-hidden transition-shadow hover:shadow-lg">
        {/* Gradient background overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-muted/20 pointer-events-none" />
        
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          <div className={`p-2 rounded-lg bg-muted/50 ${colorClass}`}>
            <Icon className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <motion.div 
            className="flex items-baseline gap-2"
            variants={valueVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="text-2xl sm:text-3xl font-bold">
              {value}
            </div>
            {trend !== undefined && (
              <div className="flex items-center gap-1">
                {getTrendIcon()}
                {getTrendText()}
              </div>
            )}
          </motion.div>
          
          <p className="text-xs text-muted-foreground">
            {description}
          </p>

          {/* Mini sparkline chart */}
          {trendData && trendData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 40 }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              className="pt-2 -mx-2"
            >
              <MiniSparkline 
                data={trendData} 
                color={colorClass.includes('blue') ? '#3b82f6' :
                       colorClass.includes('green') ? '#10b981' :
                       colorClass.includes('orange') ? '#f59e0b' :
                       '#ef4444'}
                height={40}
              />
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}